import { useState, useCallback, useEffect } from "react";

import { useTaskContext } from "@/context/taskContext";
import { TaskService } from "@/services/taskService";
import { useMapContext } from "@/hooks/useMap";
import { useAuth } from "@/hooks/useAuth";
import { useAllUsers } from "@/hooks/useUsers";
import { useTeams } from "@/hooks/useTeams";
import { useLookupValues, useTask } from "@/hooks/useTasks";
import {
  fallbackCategories,
  fallbackPriorities,
  fallbackStatuses,
} from "@/constant/fallbackData";
import type { Task, CreateTaskForm, TaskGeometry, Step } from "@/types/task";

import { toast } from "react-hot-toast";
import { PhotoUploader } from "@/components/forms/PhotoUploader";

import { GeometryPreviewMap } from "@/components/forms/GeometryPreviewMap";
import { TaskFormHeader } from "./TaskFormHeader";
import { TaskFormBasicFields } from "./TaskFormBasicFields";
import { TaskFormAssignmentFields } from "./TaskFormAssignmentFields";
import { TaskFormCategoryFields } from "./TaskFormCategoryFields";
import { TaskFormDateFields } from "./TaskFormDateFields";
import { TaskFormLayerInfo } from "./TaskFormLayerInfo";
import TaskFormSteps from "./TaskFormSteps";
import { TaskVisits } from "./TaskVisits";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { SupabaseImageService } from "@/services/supabaseImageService";

interface TaskFormProps {
  task?: Task;
  isOpen: boolean;
  onClose: () => void;
  mode?: "edit" | "view";
  onEditTask?: (task: Task) => void;
  onSuccess?: () => void;
  initialGeometry?: TaskGeometry;
  layerData?: { layerName: string; asset_field: string; asset_id: string };
}

export const TaskForm: React.FC<TaskFormProps> = ({
  task,
  onClose,
  onSuccess,
  initialGeometry,
  layerData,
  mode = "edit", // Default to edit mode
  onEditTask,
}) => {
  const {
    createTask,
    updateTask,
    loadTasks,
    categories,
    priorities,
    statuses,
    templates,
    getTemplatesByLayer,
  } = useTaskContext();
  const { userProfile } = useAuth();
  const { removeDrawnLayer } = useMapContext();

  // Fetch live task data from React Query if editing/viewing
  const { data: liveTask } = useTask(task?.id || "");

  // Use live task data if available, otherwise fall back to prop
  const currentTask = liveTask || task;

  const isEditing = !!currentTask;
  const isViewMode = mode === "view";

  // Use fallback data if main data isn't available
  const availableCategories =
    categories.length > 0 ? categories : fallbackCategories;
  const availablePriorities =
    priorities.length > 0 ? priorities : fallbackPriorities;
  const availableStatuses = statuses.length > 0 ? statuses : fallbackStatuses;

  // Show warning if using fallback data
  const usingFallbackData =
    categories.length === 0 || priorities.length === 0 || statuses.length === 0;
  const initialFormData: Partial<CreateTaskForm> & {
    photos?: string[];
    status_id?: string;
  } = {
    title: "",
    description: "",
    assignee_type: "User",
    assignee: userProfile?.id || "",
    category_id: "",
    priority_id: "",
    status_id: "",
    due_date: "",
    start_date: "",
    geom: initialGeometry,
    photos: [],
    layer_name: layerData?.layerName || "",
    asset_field: layerData?.asset_field || "",
    asset_id: layerData?.asset_id || "",
  };

  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const todayStr = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState<
    Partial<CreateTaskForm> & {
      photos?: string[];
      status_id?: string;
      steps?: Step[];
    }
  >({
    title: currentTask?.title || "",
    description: currentTask?.description || "",
    assignee_type: currentTask?.assignee_type || "User",
    assignee: currentTask?.assignee || userProfile?.id || "",
    category_id: currentTask?.category_id || "",
    priority_id: currentTask?.priority_id || "",
    status_id:
      currentTask?.status_id ||
      (!isEditing
        ? availableStatuses.find((s) => s.value === "not_started")?.id
        : ""),
    due_date: currentTask?.due_date ? currentTask.due_date.split("T")[0] : "",
    start_date: currentTask?.start_date
      ? currentTask.start_date.split("T")[0]
      : !isEditing
        ? todayStr
        : "",
    geom: currentTask?.geom || initialGeometry,
    photos: currentTask?.photos || [],
    layer_name: currentTask?.layer_name || layerData?.layerName || "",
    asset_field: currentTask?.asset_field || layerData?.asset_field || "",
    asset_id: currentTask?.asset_id || layerData?.asset_id || "",
    steps: currentTask?.steps || [],
  });

  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<"task" | "steps" | "visits">(
    "task"
  );
  const [deletedStepIds, setDeletedStepIds] = useState<string[]>([]); // Track deleted step IDs
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<
    typeof formData | null
  >(null);

  // View mode state for photos only
  const [viewModePhotoUrls, setViewModePhotoUrls] = useState<string[]>([]);

  // Get available users and teams for assignment using React Query
  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useAllUsers();
  const {
    data: teams = [],
    isLoading: teamsLoading,
    error: teamsError,
  } = useTeams();

  // Get step statuses for step status display
  const { data: stepStatuses = [] } = useLookupValues("step_status");

  // Show error toasts if data fetching fails
  useEffect(() => {
    if (usersError) {
      console.error("Error fetching users:", usersError);
      toast.error("Failed to load users");
    }
    if (teamsError) {
      console.error("Error fetching teams:", teamsError);
      toast.error("Failed to load teams");
    }
  }, [usersError, teamsError]);

  // Sync form data when currentTask changes (for real-time updates)
  useEffect(() => {
    if (currentTask) {
      const newFormData = {
        title: currentTask.title || "",
        description: currentTask.description || "",
        assignee_type: currentTask.assignee_type || "User",
        assignee: currentTask.assignee || userProfile?.id || "",
        category_id: currentTask.category_id || "",
        priority_id: currentTask.priority_id || "",
        status_id: currentTask.status_id || "",
        due_date: currentTask.due_date
          ? currentTask.due_date.split("T")[0]
          : "",
        start_date: currentTask.start_date
          ? currentTask.start_date.split("T")[0]
          : "",
        geom: currentTask.geom || initialGeometry,
        photos: currentTask.photos || [],
        layer_name: currentTask.layer_name || layerData?.layerName || "",
        asset_field: currentTask.asset_field || layerData?.asset_field || "",
        asset_id: currentTask.asset_id || layerData?.asset_id || "",
        steps: currentTask.steps || [],
      };

      setFormData(newFormData);

      // Set original data for change detection
      setOriginalFormData(JSON.parse(JSON.stringify(newFormData)));

      // Clear any deleted step tracking when task updates
      setDeletedStepIds([]);
    }
  }, [currentTask, userProfile?.id, initialGeometry, layerData]);

  // Set initial original data for new tasks
  useEffect(() => {
    if (!currentTask && !originalFormData) {
      setOriginalFormData(JSON.parse(JSON.stringify(formData)));
    }
  }, [currentTask, originalFormData, formData]);

  // Fetch signed URLs for photos in view mode
  useEffect(() => {
    if (isViewMode && formData.photos && formData.photos.length > 0) {
      SupabaseImageService.getSignedUrls(formData.photos).then((urls) => {
        setViewModePhotoUrls(urls);
      });
    }
  }, [isViewMode, formData.photos]);

  // Function to check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!originalFormData) return false;

    // Deep comparison of form data (excluding steps which are handled separately)
    const currentData = { ...formData };
    const originalData = { ...originalFormData };

    // Remove steps from comparison as they need special handling
    delete currentData.steps;
    delete originalData.steps;

    // Check if basic form fields have changed
    const formFieldsChanged =
      JSON.stringify(currentData) !== JSON.stringify(originalData);

    // Get current and original steps arrays
    const currentSteps = formData.steps || [];
    const originalSteps = originalFormData.steps || [];

    // Check if we have any temporary steps (unsaved new steps)
    const tempSteps = currentSteps.filter((step) =>
      step.id.startsWith("temp-")
    );
    const hasTempSteps = tempSteps.length > 0;

    // Check if any real steps were deleted
    const hasDeletedRealSteps = deletedStepIds.some(
      (id) => !id.startsWith("temp-")
    );

    // Simple step count comparison first (quick check)
    const currentNonTempSteps = currentSteps.filter(
      (step) => !step.id.startsWith("temp-")
    );

    // If step counts differ, we have changes
    if (currentNonTempSteps.length !== originalSteps.length) {
      return formFieldsChanged || hasTempSteps || hasDeletedRealSteps || true;
    }

    // Check if existing steps were modified (without complex sorting)
    const stepsModified = currentNonTempSteps.some((currentStep) => {
      const originalStep = originalSteps.find(
        (orig) => orig.id === currentStep.id
      );
      if (!originalStep) return true; // New non-temp step

      // Compare key properties (be more specific about what constitutes a change)
      const nameChanged =
        (currentStep.step_name || "").trim() !==
        (originalStep.step_name || "").trim();
      const descChanged =
        (currentStep.description || "").trim() !==
        (originalStep.description || "").trim();
      const assigneeTypeChanged =
        currentStep.assignee_type !== originalStep.assignee_type;
      const assigneeChanged = currentStep.assignee !== originalStep.assignee;
      const refLinkChanged =
        (currentStep.reference_link || "").trim() !==
        (originalStep.reference_link || "").trim();
      const surveyLinkChanged =
        (currentStep.survey_link || "").trim() !==
        (originalStep.survey_link || "").trim();
      const orderChanged =
        (currentStep.sequence || currentStep.step_order || 0) !==
        (originalStep.sequence || originalStep.step_order || 0);

      return (
        nameChanged ||
        descChanged ||
        assigneeTypeChanged ||
        assigneeChanged ||
        refLinkChanged ||
        surveyLinkChanged ||
        orderChanged
      );
    });

    const hasChanges =
      formFieldsChanged || hasTempSteps || hasDeletedRealSteps || stepsModified;

    return hasChanges;
  }, [formData, originalFormData, deletedStepIds]);

  // Handle close with confirmation if there are unsaved changes
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowConfirmExit(true);
    } else {
      removeDrawnLayer();
      onClose();
    }
  }, [hasUnsavedChanges, removeDrawnLayer, onClose]);

  // Confirm exit without saving
  const handleConfirmExit = useCallback(() => {
    setShowConfirmExit(false);
    removeDrawnLayer();
    onClose();
  }, [removeDrawnLayer, onClose]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.category_id) {
      newErrors.category_id = "Category is required";
    }
    if (!formData.priority_id) {
      newErrors.priority_id = "Priority is required";
    }
    if (!formData.status_id || formData.status_id.trim() === "") {
      newErrors.status_id = "Status is required";
    } else if (!availableStatuses.find((s) => s.id === formData.status_id)) {
      newErrors.status_id = "Please select a valid status";
    }
    if (!formData.assignee) {
      newErrors.assignee = "Assignee is required";
    }
    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }
    if (!formData.due_date) {
      newErrors.due_date = "Due date is required";
    }

    // Date validation: start date should not be after due date
    if (formData.start_date && formData.due_date) {
      const startDate = new Date(formData.start_date);
      const dueDate = new Date(formData.due_date);

      if (startDate > dueDate) {
        newErrors.start_date = "Start date cannot be after due date";
        newErrors.due_date = "Due date cannot be before start date";
      }
    }

    // Validate task steps if any exist
    if (formData.steps) {
      formData.steps.forEach((step, index) => {
        if (!step.step_name?.trim()) {
          newErrors[`step_${index}_name`] = `Step ${
            index + 1
          } name is required`;
        }
      });
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    if (!isValid) {
      console.log("🔍 Validation errors:", newErrors);

      // Show error snackbar with validation issues
      const errorMessages = Object.values(newErrors);
      const primaryError = errorMessages[0]; // Show the first error as main message

      if (errorMessages.length === 1) {
        toast.error(primaryError);
      } else {
        toast.error(
          `${primaryError} (${errorMessages.length - 1} more error${
            errorMessages.length > 2 ? "s" : ""
          })`
        );
      }
    }

    return isValid;
  };

  // Template application function
  const applyTemplate = useCallback(
    (templateId: string) => {
      const template = templates.find((t) => t.id === templateId);
      if (!template) {
        console.warn(`Template not found: ${templateId}`);
        return;
      }

      console.log("Applying template:", template);

      let templateSteps: Step[] = [];

      // Apply template steps to form data
      if (template.template_steps && template.template_steps.length > 0) {
        console.log("Applying template steps:", template.template_steps);

        templateSteps = template.template_steps
          .sort((a, b) => a.sequence - b.sequence) // Sort by sequence
          .map((templateStep, index) => ({
            id: `temp-${Date.now()}-${index}`, // Temporary ID for new steps
            task_id: task?.id || "", // Will be set when task is created
            step_name: templateStep.step_name,
            description: templateStep.description || "",
            step_order: index + 1,
            sequence: index + 1,
            assignee_type: templateStep.default_assignee_type || "User",
            assignee: templateStep.default_assignee || "",
            reference_link: templateStep.reference_link || "",
            survey_link: templateStep.survey_link || "",
            status_id: "",
            is_completed: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }));

        console.log("Applied template steps:", templateSteps);
      } else {
        console.log(
          "No template steps to apply - template.template_steps:",
          template.template_steps
        );
      }

      setFormData((prev) => ({
        ...prev,
        // Apply template name as title if no title exists
        title: !prev.title ? template.name : prev.title,
        // Apply template description if no description exists
        description:
          !prev.description && template.description
            ? template.description
            : prev.description,
        // Apply template metadata
        category_id: template.category_id || prev.category_id,
        priority_id: template.priority_id || prev.priority_id,
        status_id: template.status_id || prev.status_id,
        layer_name: template.layer_name || prev.layer_name,
        // Apply default assignee settings
        assignee_type: template.default_assignee_type || prev.assignee_type,
        assignee: template.default_assignee || prev.assignee,
        // Apply default due date if specified
        due_date: template.default_due_days
          ? new Date(
              Date.now() + template.default_due_days * 24 * 60 * 60 * 1000
            )
              .toISOString()
              .split("T")[0]
          : prev.due_date,
        // Apply default start date if specified
        start_date: template.default_start_days
          ? new Date(
              Date.now() + template.default_start_days * 24 * 60 * 60 * 1000
            )
              .toISOString()
              .split("T")[0]
          : prev.start_date,
        steps:
          template.template_steps && template.template_steps.length > 0
            ? templateSteps
            : [],
      }));

      toast.success(
        `Applied template: ${template.name}${
          template.template_steps?.length
            ? ` with ${template.template_steps.length} steps`
            : ""
        }`
      );
    },
    [templates, task?.id]
  );

  const handleAddStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [
        ...(prev.steps || []),
        {
          id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // More unique ID
          task_id: task?.id || "",
          step_name: "",
          description: "",
          step_order: (prev.steps?.length || 0) + 1,
          sequence: (prev.steps?.length || 0) + 1,
          assignee_type: "User",
          assignee: "",
          reference_link: "",
          survey_link: "",
          status_id: "",
          is_completed: false,
          is_expanded: true, // Start expanded by default
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ],
    }));
  };

  const handleUpdateStep = (stepId: string, field: string, value: string) => {
    // Clear step-specific validation errors
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      // Find step index for clearing step name errors
      const stepIndex = formData.steps?.findIndex((step) => step.id === stepId);
      if (stepIndex !== undefined && stepIndex >= 0 && field === "step_name") {
        const errorKey = `step_${stepIndex}_name`;
        if (newErrors[errorKey]) {
          delete newErrors[errorKey];
        }
      }

      return newErrors;
    });

    setFormData((prev) => ({
      ...prev,
      steps:
        prev.steps?.map((step) =>
          step.id === stepId ? { ...step, [field]: value } : step
        ) || [],
    }));
  };

  const handleRemoveStep = (stepId: string) => {
    // Track deletion of existing steps (not temp steps)
    if (!stepId.startsWith("temp-")) {
      setDeletedStepIds((prev) => [...prev, stepId]);
      console.log("🗑️ Marked step for deletion:", stepId);
    }

    setFormData((prev) => ({
      ...prev,
      steps:
        prev.steps
          ?.filter((step) => step.id !== stepId)
          .map((step, index) => ({
            ...step,
            step_order: index + 1,
            sequence: index + 1,
          })) || [],
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setFormData((prev) => {
      const steps = prev.steps || [];
      const oldIndex = steps.findIndex((step) => step.id === active.id);
      const newIndex = steps.findIndex((step) => step.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }

      const newSteps = arrayMove(steps, oldIndex, newIndex);

      // Update step order and sequence after reordering
      const updatedSteps = newSteps.map((step, index) => ({
        ...step,
        step_order: index + 1,
        sequence: index + 1,
      }));

      return {
        ...prev,
        steps: updatedSteps,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const taskData: CreateTaskForm & { status_id?: string } = {
        title: formData.title!,
        description: formData.description,
        assignee_type: formData.assignee_type!,
        assignee: formData.assignee!,
        category_id: formData.category_id,
        priority_id: formData.priority_id,
        status_id: formData.status_id,
        due_date: formData.due_date,
        start_date: formData.start_date,
        geom: formData.geom,
        photos: formData.photos,
        layer_name: formData.layer_name,
        asset_field: formData.asset_field,
        asset_id: formData.asset_id,
      };

      let resultTask: Task;

      if (isEditing) {
        console.log("✏️ Updating task and managing steps...");
        resultTask = await updateTask(currentTask.id, taskData);

        // Handle step updates for editing
        if (formData.steps && formData.steps.length > 0) {
          try {
            // Create new steps that don't have a real ID yet
            const newSteps = formData.steps.filter((step) =>
              step.id.startsWith("temp-")
            );
            const existingSteps = formData.steps.filter(
              (step) => !step.id.startsWith("temp-")
            );

            // Delete removed steps first
            for (const stepId of deletedStepIds) {
              console.log("🗑️ Deleting step:", stepId);
              await TaskService.deleteStep(stepId);
            }

            // Create new steps and collect their IDs
            const createdSteps: Step[] = [];
            for (const step of newSteps) {
              const createdStep = await TaskService.createStep(currentTask.id, {
                step_name: step.step_name,
                description: step.description,
                step_order: step.step_order,
                sequence: step.sequence,
                assignee_type: step.assignee_type,
                assignee: step.assignee,
                reference_link: step.reference_link,
                survey_link: step.survey_link,
                status_id: step.status_id,
                is_completed: step.is_completed,
                completed_at: step.completed_at,
                completed_by: step.completed_by,
              });
              createdSteps.push(createdStep);
            }

            // Update existing steps
            for (const step of existingSteps) {
              await TaskService.updateStep(step.id, {
                step_name: step.step_name,
                description: step.description,
                sequence: step.sequence,
                assignee_type: step.assignee_type,
                assignee: step.assignee,
                reference_link: step.reference_link,
                survey_link: step.survey_link,
                status_id: step.status_id,
                is_completed: step.is_completed,
                completed_at: step.completed_at,
                completed_by: step.completed_by,
              });
            }

            // Update form data to replace temp steps with real steps
            const updatedSteps = [
              ...existingSteps, // Keep existing steps as they are
              ...createdSteps, // Add newly created steps with real IDs
            ].sort(
              (a, b) =>
                (a.sequence || a.step_order || 0) -
                (b.sequence || b.step_order || 0)
            );

            // Update form data immediately to prevent duplicate submissions
            setFormData((prev) => ({
              ...prev,
              steps: updatedSteps,
            }));

            // Update original data after a brief delay to ensure state is updated
            setTimeout(() => {
              setOriginalFormData((prev) => ({
                ...prev,
                steps: updatedSteps,
              }));
            }, 0);
          } catch (stepError) {
            console.error("Error updating steps:", stepError);
            toast.error("Task updated but some steps failed to update");
          }
        } else if (deletedStepIds.length > 0) {
          // Handle case where all steps were deleted
          try {
            for (const stepId of deletedStepIds) {
              console.log("🗑️ Deleting step:", stepId);
              await TaskService.deleteStep(stepId);
            }
          } catch (stepError) {
            console.error("Error deleting steps:", stepError);
            toast.error("Task updated but some steps failed to delete");
          }
        }

        toast.success("Task updated successfully");

        // Clear deleted steps tracking after successful update
        setDeletedStepIds([]);
      } else {
        resultTask = await createTask(taskData);

        // Handle step creation for new tasks
        if (formData.steps && formData.steps.length > 0) {
          try {
            const createdSteps = [];
            for (const step of formData.steps) {
              const createdStep = await TaskService.createStep(resultTask.id, {
                step_name: step.step_name,
                description: step.description,
                step_order: step.step_order,
                sequence: step.sequence,
                assignee_type: step.assignee_type,
                assignee: step.assignee,
                reference_link: step.reference_link,
                survey_link: step.survey_link,
                status_id: step.status_id,
                is_completed: step.is_completed,
                completed_at: step.completed_at,
                completed_by: step.completed_by,
              });
              createdSteps.push(createdStep);
            }

            // Update resultTask with the created steps so it's available immediately
            resultTask = {
              ...resultTask,
              steps: createdSteps,
            };

            // Refetch tasks to update the cache with the new task including steps
            await loadTasks();
          } catch (stepError) {
            console.error("Error creating steps:", stepError);
            toast.error("Task created but some steps failed to create");
          }
        }

        toast.success("Task created successfully");
      }

      onSuccess?.();
      removeDrawnLayer(); // Remove the drawn layer from map

      // After successful save, we need to reset the change detection state
      // by setting both formData and originalFormData to represent the saved state
      // This prevents false "unsaved changes" detection after successful save

      // For create mode, we update the form to reflect the created task
      // For edit mode, we've already updated the form data above with real step IDs
      if (!isEditing) {
        // Close the modal
        onClose();
      } else {
        // In edit mode, update form data to reflect the saved task
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
      }

      setDeletedStepIds([]); // Clear deleted steps tracking

      // Do not close the form automatically after submit; user can close manually
    } catch (error: unknown) {
      console.error("💥 Error in handleSubmit:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save task"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    field: keyof CreateTaskForm | "status_id",
    value: string | number | boolean | undefined
  ) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Reset assignee when assignment type changes
      if (field === "assignee_type") {
        newData.assignee = "";
      }

      // Clear field-specific errors when user starts typing/selecting
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        // Clear the specific field error
        if (newErrors[field]) {
          delete newErrors[field];
        }

        // Clear assignee error when assignee_type changes
        if (field === "assignee_type" && newErrors.assignee) {
          delete newErrors.assignee;
        }

        return newErrors;
      });

      // Real-time date validation
      if (
        (field === "start_date" || field === "due_date") &&
        newData.start_date &&
        newData.due_date
      ) {
        const startDate = new Date(newData.start_date);
        const dueDate = new Date(newData.due_date);

        if (startDate > dueDate) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            start_date: "Start date cannot be after due date",
            due_date: "Due date cannot be before start date",
          }));
        } else {
          // Clear date errors if dates are valid
          setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            if (newErrors.start_date?.includes("cannot be after")) {
              delete newErrors.start_date;
            }
            if (newErrors.due_date?.includes("cannot be before")) {
              delete newErrors.due_date;
            }
            return newErrors;
          });
        }
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Clear assignee error when assignment type changes
    if (field === "assignee_type" && errors.assignee) {
      setErrors((prev) => ({ ...prev, assignee: "" }));
    }
  };

  return (
    <div
      className="fixed top-20 right-4 bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] p-6 w-[570px] z-1001"
      style={{ height: "calc(100% - 100px)" }}
    >
      <div className="w-full h-full">
        <TaskFormHeader
          title={isViewMode ? "View Task" : undefined}
          isViewMode={isViewMode}
          isEditing={isEditing}
          geometry={formData.geom || initialGeometry}
          onClose={handleClose}
          task={task}
          onEditTask={onEditTask}
          customBreadcrumbs={
            activeTab === "steps"
              ? [
                  "Location Type",
                  formData.geom?.type === "LineString"
                    ? "Line"
                    : formData.geom?.type === "Polygon"
                      ? "Polygon"
                      : "Point",
                  isEditing
                    ? isViewMode
                      ? "View Task"
                      : "Edit Task"
                    : "Create New Task",
                  "Steps",
                ]
              : activeTab === "visits"
                ? [
                    "Location Type",
                    formData.geom?.type === "LineString"
                      ? "Line"
                      : formData.geom?.type === "Polygon"
                        ? "Polygon"
                        : "Point",
                    isEditing
                      ? isViewMode
                        ? "View Task"
                        : "Edit Task"
                      : "Create New Task",
                    "Visits",
                  ]
                : undefined
          }
          showBackButton={false}
          onBack={undefined}
        />

        {/* Tab Navigation - only show for editing existing tasks */}
        {isEditing && (
          <div className="flex border-b border-gray-200 px-6 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab("task")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "task"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Task
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("steps")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "steps"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Steps ({formData.steps?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("visits")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "visits"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Visits
            </button>
          </div>
        )}

        <form
          id="task-form"
          onSubmit={handleSubmit}
          className={`px-6 py-6 overflow-y-scroll`}
          style={{ height: "calc(100% - 120px)" }}
        >
          {activeTab === "steps" ? (
            // Steps Tab Content
            <div>
              <TaskFormSteps
                steps={formData.steps || []}
                errors={errors}
                isViewMode={isViewMode}
                onAddStep={handleAddStep}
                onUpdateStep={handleUpdateStep}
                onRemoveStep={handleRemoveStep}
                onDragEnd={handleDragEnd}
              />
            </div>
          ) : activeTab === "visits" ? (
            // Visits Tab Content
            <div>
              {currentTask?.id ? (
                <TaskVisits
                  taskId={currentTask.id}
                  steps={formData.steps || []}
                  isViewMode={isViewMode}
                />
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-lg font-medium">Task ID not available</p>
                  <p className="text-sm mt-2">
                    Please save the task first to view visits
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Main Task Form Content
            <>
              {usingFallbackData && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                  ⚠️ Using default categories and priorities. Database
                  connection may be limited.
                </div>
              )}

              <div className="space-y-6">
                {/* Location Info */}
                {(formData.geom || initialGeometry) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <GeometryPreviewMap
                      geometry={formData.geom || initialGeometry}
                      height="200px"
                    />
                  </div>
                )}

                {/* Basic Fields */}
                <TaskFormBasicFields
                  formData={formData}
                  isViewMode={isViewMode}
                  selectedTemplate={selectedTemplate}
                  errors={errors}
                  isEditing={isEditing}
                  onTitleChange={(value) => handleChange("title", value)}
                  onDescriptionChange={(value) =>
                    handleChange("description", value)
                  }
                  onTemplateChange={(templateId) => {
                    if (templateId === "clear" || !templateId) {
                      // Clear template selection
                      setFormData(initialFormData);
                      setErrors({});
                      setSelectedTemplate("");
                    } else {
                      // Apply selected template
                      setFormData(initialFormData);
                      setErrors({});
                      setSelectedTemplate(templateId);
                      applyTemplate(templateId);
                    }
                  }}
                  getTemplatesByLayer={getTemplatesByLayer}
                />

                {/* Assignment Fields */}
                <TaskFormAssignmentFields
                  formData={formData}
                  errors={errors}
                  users={users}
                  teams={teams}
                  isViewMode={isViewMode}
                  usersLoading={usersLoading}
                  teamsLoading={teamsLoading}
                  onAssigneeTypeChange={(value) =>
                    handleChange("assignee_type", value)
                  }
                  onAssigneeChange={(value) => handleChange("assignee", value)}
                />

                {/* Category Fields */}
                <TaskFormCategoryFields
                  formData={formData}
                  errors={errors}
                  isViewMode={isViewMode}
                  availableCategories={availableCategories}
                  availablePriorities={availablePriorities}
                  availableStatuses={availableStatuses}
                  onCategoryChange={(value) =>
                    handleChange("category_id", value)
                  }
                  onPriorityChange={(value) =>
                    handleChange("priority_id", value)
                  }
                  onStatusChange={(value) => handleChange("status_id", value)}
                />

                {/* Date Fields */}
                <TaskFormDateFields
                  formData={formData}
                  errors={errors}
                  isViewMode={isViewMode}
                  onStartDateChange={(value) =>
                    handleChange("start_date", value)
                  }
                  onDueDateChange={(value) => handleChange("due_date", value)}
                />
                {/* Photo Upload */}
                {!isViewMode && (
                  <div>
                    <PhotoUploader
                      taskId={task?.id || "new"}
                      currentPhotos={formData.photos || []}
                      onPhotosUpdate={(photos: string[]) =>
                        setFormData((prev) => ({ ...prev, photos }))
                      }
                      disabled={loading}
                      setUploading={setPhotoUploading}
                    />
                  </div>
                )}
                {/* Show photos in view mode */}
                {isViewMode && viewModePhotoUrls.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Photos ({viewModePhotoUrls.length})
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {viewModePhotoUrls.slice(0, 4).map((photoUrl, index) => (
                        <div key={index} className="relative">
                          <img
                            src={photoUrl}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      ))}
                      {viewModePhotoUrls.length > 4 && (
                        <div className="text-sm text-gray-500 col-span-2">
                          +{viewModePhotoUrls.length - 4} more photos
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Layer Information */}
                <TaskFormLayerInfo
                  formData={formData}
                  isViewMode={isViewMode}
                />

                {/* Task Steps Summary - Only show for new tasks (not editing) */}
                {!isEditing && (
                  <div className="w-full px-4 py-3 bg-gray-100 rounded-xl outline-1 -outline-offset-1 outline-indigo-800 flex flex-col gap-3">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex justify-between items-center gap-2">
                        <div className="justify-start text-neutral-700 text-sm font-semibold font-['Open_Sans'] leading-tight">
                          Task Steps
                        </div>
                        <span className="justify-start text-zinc-500 text-xs font-semibold font-['Open_Sans'] leading-none">
                          ({formData.steps?.length || 0} Step
                          {(formData.steps?.length || 0) !== 1 ? "s" : ""})
                        </span>
                      </div>
                      <div className="flex justify-center items-center">
                        <button
                          type="button"
                          onClick={() => setActiveTab("steps")}
                          className="px-4 py-3 rounded-[10px] cursor-pointer gap-2 outline-1 -outline-offset-1 outline-indigo-800 flex items-center justify-start text-indigo-800  hover:bg-white text-xs font-semibold font-['Open_Sans'] leading-none"
                        >
                          {formData.steps && formData.steps.length > 0 ? (
                            "View Steps"
                          ) : (
                            <span className="">+ Add Steps</span>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Display step names */}
                    {formData.steps && formData.steps.length > 0 && (
                      <div className="flex flex-col justify-between gap-1">
                        {(formData.steps || [])
                          .sort((a, b) => {
                            // Sort by sequence first, then by step_order as fallback
                            const aOrder = a.sequence || a.step_order || 0;
                            const bOrder = b.sequence || b.step_order || 0;
                            return aOrder - bOrder;
                          })
                          .map((step, index) => (
                            <div
                              key={step.id || `temp-${index}`}
                              className="flex justify-between text-xs text-gray-600 font-['Open_Sans'] leading-relaxed"
                            >
                              <div>
                                {" "}
                                {index + 1}. {step.step_name || "Untitled Step"}
                              </div>
                              <div className="text-[10px] text-gray-400 ml-2">
                                {step.status_id
                                  ? `${
                                      stepStatuses.find(
                                        (s) => s.id === step.status_id
                                      )?.label || "Unknown"
                                    }`
                                  : "No status"}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </form>

        {/* Action Buttons */}
        {!isViewMode && (
          <div className="flex justify-center mt-1">
            <button
              type="submit"
              form="task-form"
              disabled={loading || photoUploading}
              className="flex justify-center self-stretch w-full px-6 py-3 rounded-[10px] items-center gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
            >
              {(() => {
                if (loading) return "Saving...";
                if (isEditing) {
                  if (activeTab === "steps") {
                    return `Update Steps`;
                  }
                  return "Update Task";
                }
                return (formData.steps?.length || 0) > 0
                  ? `Create Task + ${formData.steps?.length} Steps`
                  : "Create Task";
              })()}
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal for Unsaved Changes */}
      <ConfirmationModal
        open={showConfirmExit}
        onOpenChange={setShowConfirmExit}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave without saving?"
        onConfirm={handleConfirmExit}
        confirmText="Leave Without Saving"
        cancelText="Stay"
      />
    </div>
  );
};
